import React, { useEffect, useMemo, useRef, useState } from "react";
import { getMediaByOperator, uploadMedia } from "../../services/api";
import { FiUpload, FiX, FiFile, FiImage, FiVideo, FiEye } from "react-icons/fi";
import { MdOutlinePermMedia } from "react-icons/md";
import globalStyles from "../../styles/globalStyles";

const PRIMARY = "#534AB7";
const PRIMARY_50 = "#EEEDFE";
const PRIMARY_100 = "#CECBF6";
const PRIMARY_200 = "#AFA9EC";

const API_ORIGIN =
  process.env.REACT_APP_URL || "https://sales-server.itsvivek.in";

const DOCUMENT_VIEW_URL =
  process.env.REACT_APP_GOOGLE_DOCS_VIEWER_URL ||
  "https://docs.google.com/gview?embedded=true&url=";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth <= breakpoint,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 12,
    boxSizing: "border-box",
  },

  modal: {
    background: "#fff",
    width: 900,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 26px 18px",
    borderBottom: "1px solid #e2e8f0",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: PRIMARY_50,
    border: `1px solid ${PRIMARY_100}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: PRIMARY,
    flexShrink: 0,
  },

  headerTitle: {
    fontSize: 19,
    fontWeight: 700,
    color: "#1e293b",
    margin: 0,
  },

  headerSub: {
    fontSize: 14,
    fontWeight: 700,
    color: "#64748b",
    marginTop: 4,
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 18,
    color: "#6b7280",
  },

  body: {
    padding: "20px 26px 26px",
  },

  uploadCard: {
    background: PRIMARY_50,
    border: `1px solid ${PRIMARY_100}`,
    borderRadius: 10,
    padding: "18px 18px",
    marginBottom: 20,
  },

  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: PRIMARY,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    marginBottom: 11,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  dropZone: {
    background: "#fff",
    border: `1.5px dashed ${PRIMARY_200}`,
    borderRadius: 10,
    padding: "16px 18px",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  dzIcon: {
    width: 38,
    height: 38,
    borderRadius: 9,
    background: PRIMARY_50,
    border: `1px solid ${PRIMARY_100}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: PRIMARY,
    flexShrink: 0,
  },

  dzTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1e293b",
  },

  dzSub: {
    fontSize: 11,
    color: "#94a3b8",
    marginTop: 3,
  },

  browseBtn: {
    marginLeft: "auto",
    background: PRIMARY,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },

  hiddenInput: {
    display: "none",
  },

  previewCard: {
    background: "#fff",
    border: `1px solid ${PRIMARY_100}`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  previewThumb: {
    width: 60,
    height: 60,
    borderRadius: 8,
    objectFit: "cover",
    border: `1px solid ${PRIMARY_100}`,
    flexShrink: 0,
  },

  previewIconBox: (color, bg) => ({
    width: 60,
    height: 60,
    borderRadius: 8,
    background: bg,
    border: `1px solid ${PRIMARY_100}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: color,
    flexShrink: 0,
  }),

  previewInfo: {
    flex: 1,
    minWidth: 0,
  },

  previewName: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  previewMeta: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 4,
  },

  previewRemoveBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#dc2626",
    flexShrink: 0,
  },

  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
  },

  uploadBtn: (hasFile, uploading) => ({
    padding: "7px 18px",
    background: "none",
    border: "1.5px solid #464749",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: hasFile && !uploading ? PRIMARY : "#464749",
    cursor: hasFile ? "pointer" : "default",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }),

  errBox: {
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    gap: 10,
    flexWrap: "wrap",
  },

  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  filesLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  countBadge: {
    background: PRIMARY_50,
    color: PRIMARY,
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 9px",
    borderRadius: 20,
    border: `1px solid ${PRIMARY_100}`,
  },

  filterWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  filterSelect: {
    padding: "7px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 12,
    color: "#374151",
    background: "#f8fafc",
    cursor: "pointer",
    outline: "none",
  },

  clearFilterBtn: {
    padding: "7px 10px",
    border: `1px solid ${PRIMARY_100}`,
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    color: PRIMARY,
    background: PRIMARY_50,
    cursor: "pointer",
  },

  table: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },

  th: {
    background: "#b1b9c1",
    padding: "8px 14px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },

  fnameCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: "#1e293b",
  },

  badge: (type) => {
    const map = {
      image: { background: "#dcfce7", color: "#10b981" },
      video: { background: "#dbeafe", color: "#2563eb" },
      pdf: { background: "#fee2e2", color: "#dc2626" },
      document: { background: "#fef3c7", color: "#d97706" },
    };
    return {
      padding: "3px 9px",
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 700,
      textTransform: "capitalize",
      ...(map[type] || { background: "#f1f5f9", color: "#64748b" }),
    };
  },

  emptyTd: {
    textAlign: "center",
    padding: 28,
    color: "#94a3b8",
    fontSize: 13,
  },

  loading: {
    fontSize: 13,
    color: "#64748b",
    padding: "12px 0",
  },

  viewBtn: {
    border: "none",
    background: "#f8fafc",
    color: "#374151",
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  viewerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.78)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
    boxSizing: "border-box",
  },

  viewerCard: {
    background: "#fff",
    width: "min(800px, 96vw)",
    height: "min(560px, 92vh)",
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 18px 45px rgba(0,0,0,0.28)",
  },

  viewerHeader: {
    minHeight: 50,
    padding: "10px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  },

  viewerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  viewerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  viewerImage: {
    display: "block",
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    borderRadius: 10,
  },

  viewerFrame: {
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: 10,
    background: "#fff",
  },

  viewerVideo: {
    maxWidth: "100%",
    maxHeight: "100%",
    borderRadius: 10,
    background: "#000",
  },

  unsupportedBox: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
  },

  previewLoader: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(248,250,252,0.9)",
    color: PRIMARY,
    fontSize: 13,
    fontWeight: 700,
    zIndex: 2,
  },

  viewerBody: {
    flex: 1,
    minHeight: 0,
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
    overflow: "hidden",
    position: "relative",
  },
};

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getLocalFileType = (file) => {
  if (!file) return "document";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "document";
};

const getFileType = (file) => {
  const type = String(
    file?.type || file?.fileType || file?.mimeType || "",
  ).toLowerCase();
  const name = String(
    file?.name ||
      file?.fileName ||
      file?.originalName ||
      file?.filename ||
      file?.url ||
      file?.fileUrl ||
      file?.path ||
      "",
  ).toLowerCase();

  if (
    type.includes("image") ||
    /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(name)
  )
    return "image";

  if (type.includes("video") || /\.(mp4|mov|avi|mkv|webm)(\?.*)?$/i.test(name))
    return "video";

  if (type.includes("pdf") || /\.pdf(\?.*)?$/i.test(name)) return "pdf";

  return "document";
};

const getFileIcon = (type) => {
  if (type === "image") return FiImage;
  if (type === "video") return FiVideo;
  return FiFile;
};

const getFileName = (file) =>
  file?.originalName ||
  file?.fileName ||
  file?.name ||
  file?.filename ||
  "Unnamed File";

const getUploadedBy = (file) => {
  if (typeof file?.uploadedBy === "object")
    return file.uploadedBy?.name || file.uploadedBy?.email || "—";
  return file?.uploadedByName || file?.createdBy?.name || "—";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const getFileDateValue = (file) =>
  file?.createdAt ||
  file?.uploadedAt ||
  file?.updatedAt ||
  file?.created_at ||
  file?.uploaded_at ||
  file?.date ||
  "";

function FilePreview({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const localType = getLocalFileType(file);

  useEffect(() => {
    if (localType !== "image") return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, localType]);

  const iconConfig = {
    video: { icon: <FiVideo size={24} />, color: "#2563eb", bg: "#dbeafe" },
    document: { icon: <FiFile size={24} />, color: "#d97706", bg: "#fef3c7" },
    image: { icon: <FiImage size={24} />, color: PRIMARY, bg: PRIMARY_50 },
  };

  const cfg = iconConfig[localType] || iconConfig.document;

  return (
    <div style={s.previewCard}>
      {localType === "image" && previewUrl ? (
        <img src={previewUrl} alt="preview" style={s.previewThumb} />
      ) : (
        <div style={s.previewIconBox(cfg.color, cfg.bg)}>{cfg.icon}</div>
      )}

      <div style={s.previewInfo}>
        <div style={s.previewName}>{file.name}</div>
        <div style={s.previewMeta}>
          {localType.charAt(0).toUpperCase() + localType.slice(1)}
          {file.size ? `  •  ${formatBytes(file.size)}` : ""}
        </div>
      </div>

      <button
        type="button"
        style={s.previewRemoveBtn}
        onClick={onRemove}
        title="Remove"
      >
        <FiX size={13} />
      </button>
    </div>
  );
}

export default function MediaGallery({ operator, onClose }) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const isMobile = useIsMobile();

  const [viewFile, setViewFile] = useState(null);

  const getFileUrl = (file) => {
    const rawUrl =
      file?.url ||
      file?.fileUrl ||
      file?.path ||
      file?.location ||
      file?.secureUrl ||
      file?.filename ||
      "";

    if (!rawUrl) return "";

    const normalizedUrl = String(rawUrl).replace(/\\/g, "/").trim();

    if (
      normalizedUrl.startsWith("http://") ||
      normalizedUrl.startsWith("https://") ||
      normalizedUrl.startsWith("blob:") ||
      normalizedUrl.startsWith("data:")
    ) {
      return encodeURI(normalizedUrl);
    }

    if (normalizedUrl.startsWith("/uploads/")) {
      return encodeURI(`${API_ORIGIN}${normalizedUrl}`);
    }

    if (normalizedUrl.startsWith("uploads/")) {
      return encodeURI(`${API_ORIGIN}/${normalizedUrl}`);
    }

    const looksLikeOnlyFileName =
      !normalizedUrl.includes("/") &&
      /\.(jpg|jpeg|png|webp|gif|svg|mp4|mov|avi|mkv|webm|pdf)$/i.test(
        normalizedUrl,
      );

    if (looksLikeOnlyFileName) {
      return encodeURI(`${API_ORIGIN}/uploads/${normalizedUrl}`);
    }

    return encodeURI(`${API_ORIGIN}/${normalizedUrl.replace(/^\/+/, "")}`);
  };

  const handleViewFile = (file) => {
    const url = getFileUrl(file);

    if (!url) {
      setErr("File URL not found");
      return;
    }

    const previewType = getFileType(file);

    setPreviewLoading(previewType !== "document");

    setViewFile({
      ...file,
      previewUrl: url,
      previewType,
    });
  };

  const loadFiles = async () => {
    if (!operator?._id) return;
    setLoading(true);
    setErr("");
    try {
      const res = await getMediaByOperator(operator._id);
      const apiFiles =
        res?.data?.data?.files || res?.data?.files || res?.data?.data || [];
      setFiles(Array.isArray(apiFiles) ? apiFiles : []);
    } catch (error) {
      setErr(error?.response?.data?.message || "Failed to load media files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [operator?._id]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!file || !operator?._id) return;
    setUploading(true);
    setErr("");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("operatorId", operator._id);
    try {
      await uploadMedia(formData);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadFiles();
    } catch (error) {
      setErr(error?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const getDateKey = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const filteredFiles = useMemo(() => {
    return files.filter((f) => {
      const type = getFileType(f);
      const fileDate = getDateKey(getFileDateValue(f));

      if (typeFilter && type !== typeFilter) return false;
      if (dateFilter && fileDate !== dateFilter) return false;

      return true;
    });
  }, [files, typeFilter, dateFilter]);

  return (
    <>
      <div style={s.overlay} onClick={onClose}>
        <div
          style={{
            ...s.modal,
            ...(isMobile
              ? {
                  width: "94vw",
                  maxWidth: "94vw",
                  maxHeight: "92vh",
                }
              : {}),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              ...s.modalHeader,
              ...(isMobile ? { padding: "16px 16px 14px" } : {}),
            }}
          >
            <div style={s.headerLeft}>
              <div style={s.headerIcon}>
                <MdOutlinePermMedia size={20} />
              </div>
              <div>
                <h2 style={s.headerTitle}>Media Gallery</h2>
                <div style={s.headerSub}>
                  {operator?.name || operator?.businessName || "Operator"}
                </div>
              </div>
            </div>
            <button type="button" style={s.closeBtn} onClick={onClose}>
              <FiX size={15} />
            </button>
          </div>

          <div
            style={{
              ...s.body,
              ...(isMobile ? { padding: "14px 14px 18px" } : {}),
            }}
          >
            <div style={s.uploadCard}>
              {err && (
                <div style={s.errBox}>
                  <FiX size={13} /> {err}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,application/pdf"
                style={s.hiddenInput}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

              {!file && (
                <>
                  <div
                    style={{
                      ...s.dropZone,
                      ...(isMobile
                        ? {
                            flexDirection: "column",
                            alignItems: "stretch",
                            textAlign: "center",
                          }
                        : {}),
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={openFilePicker}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        openFilePicker();
                      }
                    }}
                  >
                    <div style={s.dzIcon}>
                      <FiUpload size={17} />
                    </div>

                    <div>
                      <div style={s.dzTitle}>Choose media file</div>
                      <div style={s.dzSub}>
                        Images, MP4 videos, and PDF files allowed
                      </div>
                    </div>

                    <button
                      type="button"
                      style={{
                        ...s.browseBtn,
                        ...(isMobile ? { marginLeft: 0, width: "100%" } : {}),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openFilePicker();
                      }}
                    >
                      Browse
                    </button>
                  </div>
                </>
              )}

              {file && (
                <FilePreview
                  file={file}
                  onRemove={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                />
              )}

              <div
                style={{
                  ...s.btnRow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...s.uploadBtn(!!file, uploading),
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                  disabled={!file || uploading}
                  onClick={handleUpload}
                >
                  <FiUpload size={14} />
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>

            <div
              style={{
                ...globalStyles.toolbar,
                ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
              }}
            >
              <div style={s.toolbarLeft}>
                <div style={s.filesLabel}>
                  <FiFile size={13} style={{ color: PRIMARY }} />
                  Uploaded Files
                </div>
                <span style={s.countBadge}>{filteredFiles.length} files</span>
              </div>

              <div
                style={{
                  ...s.filterWrap,
                  ...(isMobile
                    ? {
                        width: "100%",
                        flexDirection: "column",
                        alignItems: "stretch",
                      }
                    : {}),
                }}
              >
                <select
                  style={{
                    ...s.filterSelect,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">Select Type</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="pdf">PDF</option>
                  <option value="document">Document</option>
                </select>
                <input
                  type="date"
                  style={{
                    ...s.filterSelect,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                />
                {(typeFilter || dateFilter) && (
                  <button
                    type="button"
                    style={{
                      ...s.clearFilterBtn,
                      ...(isMobile ? { width: "100%" } : {}),
                    }}
                    onClick={() => {
                      setTypeFilter("");
                      setDateFilter("");
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div style={s.loading}>Loading media files...</div>
            ) : (
              <div style={globalStyles.tableScroll}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>File Name</th>
                      <th style={s.th}>Type</th>
                      <th style={s.th}>Uploaded By</th>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFiles.length > 0 ? (
                      filteredFiles.map((item) => {
                        const type = getFileType(item);
                        const Icon = getFileIcon(type);
                        return (
                          <tr key={item._id || item.id || getFileName(item)}>
                            <td style={s.td}>
                              <div style={s.fnameCell}>
                                <Icon size={15} color="#94a3b8" />
                                {getFileName(item)}
                              </div>
                            </td>
                            <td style={s.td}>
                              <span style={s.badge(type)}>{type}</span>
                            </td>
                            <td style={s.td}>{getUploadedBy(item)}</td>
                            <td style={s.td}>
                              {formatDate(getFileDateValue(item))}
                            </td>
                            <td style={s.td}>
                              <button
                                type="button"
                                title="View"
                                style={s.viewBtn}
                                onClick={() => handleViewFile(item)}
                              >
                                <FiEye size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ ...s.td, ...s.emptyTd }}>
                          No media files found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {viewFile && (
        <div
          style={s.viewerOverlay}
          onClick={() => {
            setViewFile(null);
            setPreviewLoading(false);
          }}
        >
          <div
            style={{
              ...s.viewerCard,
              ...(isMobile ? { width: "96vw", height: "88vh" } : {}),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={s.viewerHeader}>
              <div style={s.viewerTitle}>{getFileName(viewFile)}</div>

              <div style={s.viewerActions}>
                <button
                  type="button"
                  style={s.closeBtn}
                  onClick={() => {
                    setViewFile(null);
                    setPreviewLoading(false);
                  }}
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>

            <div style={s.viewerBody}>
              {previewLoading && (
                <div style={s.previewLoader}>Loading preview...</div>
              )}

              {viewFile.previewType === "image" && (
                <img
                  src={viewFile.previewUrl}
                  alt={getFileName(viewFile)}
                  style={s.viewerImage}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                />
              )}

              {viewFile.previewType === "video" && (
                <video
                  controls
                  style={s.viewerVideo}
                  onLoadedData={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                >
                  <source src={viewFile.previewUrl} />
                  Your browser does not support video.
                </video>
              )}

              {viewFile.previewType === "pdf" && (
                <iframe
                  src={`${DOCUMENT_VIEW_URL}${encodeURIComponent(
                    viewFile.previewUrl,
                  )}`}
                  title={getFileName(viewFile)}
                  style={s.viewerFrame}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                />
              )}

              {viewFile.previewType === "document" && (
                <div style={s.unsupportedBox}>
                  Preview not available for this file type.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
